import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { config } from '@/infrastructure/config'

/**
 * Authentication. With VITE_SUPABASE_URL + VITE_SUPABASE_PUBLISHABLE_KEY it uses Supabase Auth
 * (email + password now, Google OAuth later). Without them it runs a demo login with fictitious
 * accounts, so the app can be designed and reviewed before the backend exists.
 */

export interface AuthUser {
  id: string
  email: string
  name: string
}

export interface AuthSession {
  user: AuthUser
  /** JWT sent to the API as `Authorization: Bearer …` (null in demo mode). */
  accessToken: string | null
}

export interface AuthProvider {
  readonly mode: 'supabase' | 'demo'
  getSession(): Promise<AuthSession | null>
  onChange(cb: (s: AuthSession | null) => void): () => void
  signIn(email: string, password: string): Promise<void>
  signUp(email: string, password: string, name: string): Promise<{ needsConfirmation: boolean }>
  resetPassword(email: string): Promise<void>
  signOut(): Promise<void>
}

// ---------------------------------------------------------------------------
// Supabase
// ---------------------------------------------------------------------------

function supabaseAuth(client: SupabaseClient): AuthProvider {
  const toSession = (s: { access_token: string; user: { id: string; email?: string; user_metadata?: Record<string, unknown> } } | null): AuthSession | null =>
    s
      ? {
          accessToken: s.access_token,
          user: {
            id: s.user.id,
            email: s.user.email ?? '',
            name: String(s.user.user_metadata?.full_name ?? s.user.user_metadata?.name ?? s.user.email ?? ''),
          },
        }
      : null
  return {
    mode: 'supabase',
    async getSession() {
      const { data } = await client.auth.getSession()
      return toSession(data.session)
    },
    onChange(cb) {
      const { data } = client.auth.onAuthStateChange((_e, s) => cb(toSession(s)))
      return () => data.subscription.unsubscribe()
    },
    async signIn(email, password) {
      const { error } = await client.auth.signInWithPassword({ email, password })
      if (error) throw error
    },
    async signUp(email, password, name) {
      const { data, error } = await client.auth.signUp({
        email,
        password,
        options: { data: { full_name: name }, emailRedirectTo: `${window.location.origin}/` },
      })
      if (error) throw error
      return { needsConfirmation: !data.session }
    },
    async resetPassword(email) {
      const { error } = await client.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/` })
      if (error) throw error
    },
    async signOut() {
      await client.auth.signOut()
    },
  }
}

// ---------------------------------------------------------------------------
// Demo (no backend): fictitious accounts, any password
// ---------------------------------------------------------------------------

const DEMO_KEY = 'nolens-vacations.demo-session'

export const DEMO_ACCOUNTS: Array<{ email: string; name: string; label: string }> = [
  { email: 'ana@demo.nolens.app', name: 'Ana Ribeiro', label: 'Colaboradora' },
  { email: 'rui@demo.nolens.app', name: 'Rui Matos', label: 'Gestor' },
  { email: 'marta@demo.nolens.app', name: 'Marta Lopes', label: 'Recursos Humanos' },
  { email: 'admin@demo.nolens.app', name: 'Sofia Andrade', label: 'Administradora' },
]

function demoAuth(): AuthProvider {
  const listeners = new Set<(s: AuthSession | null) => void>()
  const read = (): AuthSession | null => {
    try {
      const raw = localStorage.getItem(DEMO_KEY)
      return raw ? (JSON.parse(raw) as AuthSession) : null
    } catch {
      return null
    }
  }
  const write = (s: AuthSession | null) => {
    try {
      if (s) localStorage.setItem(DEMO_KEY, JSON.stringify(s))
      else localStorage.removeItem(DEMO_KEY)
    } catch {
      // private mode: session lives only in memory
    }
    listeners.forEach((l) => l(s))
  }
  const sessionFor = (email: string, name?: string): AuthSession => {
    const known = DEMO_ACCOUNTS.find((a) => a.email === email.toLowerCase())
    return { accessToken: null, user: { id: `demo:${email.toLowerCase()}`, email: email.toLowerCase(), name: name ?? known?.name ?? email.split('@')[0]! } }
  }
  return {
    mode: 'demo',
    async getSession() {
      return read()
    },
    onChange(cb) {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    async signIn(email) {
      write(sessionFor(email))
    },
    async signUp(email, _password, name) {
      write(sessionFor(email, name))
      return { needsConfirmation: false }
    },
    async resetPassword() {},
    async signOut() {
      write(null)
    },
  }
}

export const auth: AuthProvider =
  config.supabaseUrl && config.supabaseKey ? supabaseAuth(createClient(config.supabaseUrl, config.supabaseKey)) : demoAuth()
